import { startTransition } from "react";
import type { ConnectionState } from "convex/browser";
import type {
  AuthTokenFetcher,
  ConvexReactClient,
  ConvexReactClientOptions,
  Watch,
  WatchQueryOptions,
} from "convex/react";
import type {
  ArgsAndOptions,
  FunctionReference,
  FunctionReference_future,
  FunctionReturnType,
} from "convex/server";
import { convexRequestIdOf } from "@shared/telemetry/convex-trace";
import { TracedConvexReactClient } from "@shared/telemetry/traced-convex-client";
import { currentPosthogSessionId } from "@/lib/analytics/posthog-session";
import { recordConvexRequestId } from "@/lib/telemetry/convex-request-ids";

function asTransition<Value>(
  listener: ((value: Value) => void) | undefined,
): ((value: Value) => void) | undefined {
  if (listener === undefined) return undefined;
  return (value) => startTransition(() => listener(value));
}

const CLOSED_CONNECTION_STATE: ConnectionState = {
  hasInflightRequests: false,
  isWebSocketConnected: false,
  timeOfOldestInflightRequest: null,
  hasEverConnected: true,
  connectionCount: 0,
  connectionRetries: 0,
  inflightMutations: 0,
  inflightActions: 0,
};

const INERT_WATCH: Watch<never> = {
  onUpdate: () => () => {},
  localQueryResult: () => undefined,
  journal: () => undefined,
};

// WHY: a client closes only when the protected session ends, and ProtectedSession then replaces every child with the reconnect prompt, so nothing awaiting these promises outlives the close; rejecting would surface as unhandled rejections in error tracking.
function never<Result>(): Promise<Result> {
  return new Promise<Result>(() => {});
}

class BrowserConvexReactClient extends TracedConvexReactClient {
  private backendAnsweredAuth = false;
  private isClosed = false;

  get hasBackendAnsweredAuth(): boolean {
    return this.backendAnsweredAuth;
  }

  override close(): Promise<void> {
    this.isClosed = true;
    return super.close();
  }

  override mutation(...call: Parameters<TracedConvexReactClient["mutation"]>) {
    if (this.isClosed) return never<Awaited<ReturnType<TracedConvexReactClient["mutation"]>>>();
    return super.mutation(...call);
  }

  override action(...call: Parameters<TracedConvexReactClient["action"]>) {
    if (this.isClosed) return never<Awaited<ReturnType<TracedConvexReactClient["action"]>>>();
    return super.action(...call);
  }

  override watchQuery<Query extends FunctionReference<"query"> | FunctionReference_future<"query">>(
    query: Query,
    ...argsAndOptions: ArgsAndOptions<Query, WatchQueryOptions>
  ): Watch<FunctionReturnType<Query>> {
    if (this.isClosed) return INERT_WATCH;
    const watch = super.watchQuery(query, ...argsAndOptions);
    return {
      ...watch,
      onUpdate: (callback: () => void) => (this.isClosed ? () => {} : watch.onUpdate(callback)),
    };
  }

  override connectionState(): ConnectionState {
    if (this.isClosed) return CLOSED_CONNECTION_STATE;
    return super.connectionState();
  }

  override subscribeToConnectionState(callback: (state: ConnectionState) => void): () => void {
    if (this.isClosed) return () => {};
    return super.subscribeToConnectionState(callback);
  }

  override setAuth(
    fetchToken: AuthTokenFetcher,
    onChange?: (isAuthenticated: boolean) => void,
    onRefreshChange?: (isRefreshing: boolean) => void,
  ) {
    if (this.isClosed) return;
    this.backendAnsweredAuth = false;
    const notifySignedIn = asTransition(onChange);
    super.setAuth(
      fetchToken,
      (isAuthenticated) => {
        this.backendAnsweredAuth = true;
        if (isAuthenticated) notifySignedIn?.(isAuthenticated);
        else onChange?.(isAuthenticated);
      },
      asTransition(onRefreshChange),
    );
  }

  override clearAuth() {
    if (this.isClosed) return;
    this.backendAnsweredAuth = false;
    super.clearAuth();
  }
}

export function hasBackendAnsweredAuth(client: ConvexReactClient): boolean {
  return (
    client instanceof BrowserConvexReactClient && client.hasBackendAnsweredAuth
  );
}

export function createBrowserConvexClient(
  address: string,
  options?: ConvexReactClientOptions,
): TracedConvexReactClient {
  return new BrowserConvexReactClient(address, options, {
    traceContext: () => {
      const sessionId = currentPosthogSessionId();
      return sessionId === undefined ? undefined : { sessionId };
    },
    onFailure: (functionName, error) =>
      recordConvexRequestId(convexRequestIdOf(error), functionName),
  });
}
